import { useRef, useState } from 'react';
import type { ProductCategory } from '@/mocks/products';

interface ProductCarouselProps {
  category: ProductCategory;
}

export default function ProductCarousel({ category }: ProductCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  };

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollAmount = 300;
    el.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
    setTimeout(checkScroll, 300);
  };

  return (
    <div className="mb-12 md:mb-16">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-5 md:mb-6">
        <div className="flex items-center gap-3">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
            {category.name}
          </h2>
          <span className="text-xs md:text-sm text-foreground-400 bg-background-100 px-2.5 py-1 rounded-full">
            {category.products.length} items
          </span>
        </div>
        <a
          href={category.categoryUrl}
          target="_blank"
          rel="noopener nofollow"
          className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
        >
          View All
          <i className="ri-arrow-right-up-line text-xs"></i>
        </a>
      </div>

      {/* Carousel Container */}
      <div className="relative group">
        {/* Left Arrow */}
        <button
          onClick={() => scroll('left')}
          className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/95 backdrop-blur-sm border border-background-200/70 shadow-lg flex items-center justify-center transition-all duration-200 hover:bg-primary-50 hover:border-primary-300/60 ${
            canScrollLeft
              ? 'opacity-100 pointer-events-auto'
              : 'opacity-0 pointer-events-none'
          } hidden md:flex`}
        >
          <i className="ri-arrow-left-s-line text-foreground-600 text-lg"></i>
        </button>

        {/* Right Arrow */}
        <button
          onClick={() => scroll('right')}
          className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/95 backdrop-blur-sm border border-background-200/70 shadow-lg flex items-center justify-center transition-all duration-200 hover:bg-primary-50 hover:border-primary-300/60 ${
            canScrollRight
              ? 'opacity-100 pointer-events-auto'
              : 'opacity-0 pointer-events-none'
          } hidden md:flex`}
        >
          <i className="ri-arrow-right-s-line text-foreground-600 text-lg"></i>
        </button>

        {/* Scrollable Track */}
        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className="flex gap-4 md:gap-5 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory scroll-smooth"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {category.products.map((product) => (
            <a
              key={product.id}
              href={product.url}
              target="_blank"
              rel="noopener nofollow"
              className="flex-shrink-0 w-[160px] sm:w-[200px] md:w-[240px] snap-start group/card"
            >
              {/* Image */}
              <div className="relative aspect-square rounded-xl overflow-hidden bg-background-100 mb-3 border border-background-200/70">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500"
                  width={240}
                  height={240}
                  loading="lazy"
                />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover/card:bg-black/10 transition-colors duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover/card:opacity-100 transition-opacity duration-300 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1.5 text-xs font-medium text-foreground-800 shadow-lg">
                    View on kaspreadsheet
                  </div>
                </div>
              </div>

              {/* Info */}
              <div className="space-y-1.5">
                <h3 className="text-sm font-medium text-foreground-800 leading-snug line-clamp-2 group-hover/card:text-primary-600 transition-colors">
                  {product.name}
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-primary-600">
                    ¥{product.priceCNY}
                  </span>
                  <span className="text-xs text-foreground-400">
                    ≈ ${product.priceUSD}
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}